import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface TrafficSource {
	utm_source: string;
	utm_medium: string;
	utm_campaign: string;
	utm_term: string;
	utm_content: string;
	gclid: string | null;
	client_id: string | null;
}

@Injectable({ providedIn: 'root' })
export class TrafficSourceService {
	private readonly COOKIE_NAME = 'sbjs_current';
	private readonly COOKIE_NAME_ADD = 'sbjs_current_add';
	private readonly LS_KEY = 'traffic_source';

	private readonly SAFE = /^[a-z0-9_-]{1,64}$/i;
	private readonly MAX_COOKIE_LENGTH = 512;
	private readonly searchEngines = [
		// глобальные
		'google', 'bing', 'yahoo', 'duckduckgo', 'brave', 'startpage',
		// восточная Азия
		'baidu', 'sogou', 'naver', 'daum',
		// Европа
		'yandex', 'seznam', 'qwant', 'ecosia',
		// США и прочие
		'ask', 'aol', 'gigablast', 'gibiru',
		// исторические/нишевые
		'rambler', 'lycos'
	];

	constructor(@Inject(DOCUMENT) private doc: Document) { }

	private clean(v: string | null): string | null {
		return v && this.SAFE.test(v) ? v : null;
	}

	trackTrafficSource(): TrafficSource {
		const current = this.detectCurrentSource();
		const stored = this.getStoredSource();

		const final = current.utm_source === '(direct)' && stored ? stored : current;

		this.persist(final);
		return final;
	}

	private detectCurrentSource(): TrafficSource {
		const params = new URLSearchParams(this.doc.location.search);

		const gclid = this.clean(params.get('gclid'));
		const utmSource = this.clean(params.get('utm_source'));
		const utmMedium = this.clean(params.get('utm_medium'));
		const utmCampaign = this.clean(params.get('utm_campaign'));
		const utmTerm = this.clean(params.get('utm_term'));
		const utmContent = this.clean(params.get('utm_content'));

		if (gclid) {
			return this.buildSource({
				utm_source: 'google',
				utm_medium: 'cpc',
				utm_campaign: utmCampaign ?? undefined,
				utm_term: utmTerm ?? undefined,
				utm_content: utmContent ?? undefined,
				gclid
			});
		}

		if (utmSource || utmMedium) {
			return this.buildSource({
				utm_source: utmSource ?? undefined,
				utm_medium: utmMedium ?? undefined,
				utm_campaign: utmCampaign ?? undefined,
				utm_term: utmTerm ?? undefined,
				utm_content: utmContent ?? undefined,
				gclid: null
			});
		}

		const ref = this.doc.referrer;
		if (ref) {
			const a = this.doc.createElement('a');
			a.href = ref;
			const domain = a.hostname.replace(/^www\./, '');
			const engine = this.searchEngines.find(se => domain.includes(se));

			return this.buildSource({
				utm_source: engine ? engine : domain,
				utm_medium: engine ? 'organic' : 'referral',
				gclid: null
			});
		}

		return this.buildSource({
			utm_source: '(direct)',
			utm_medium: '(none)',
			gclid: null
		});
	}

	private buildSource(partial: Partial<TrafficSource>): TrafficSource {
		const none = '(none)';
		return {
			utm_source: partial.utm_source ?? none,
			utm_medium: partial.utm_medium ?? none,
			utm_campaign: partial.utm_campaign ?? none,
			utm_term: partial.utm_term ?? none,
			utm_content: partial.utm_content ?? none,
			gclid: partial.gclid ?? null,
			client_id: this.extractClientId()
		};
	}

	private extractClientId(): string | null {
		const gaCookie = this.getCookie('_ga');
		if (!gaCookie) return null;
		const parts = gaCookie.split('.');
		return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
	}

	private persist(src: TrafficSource): void {
		const ttlDays = 30;

		this.setCookie(this.COOKIE_NAME, JSON.stringify({
			src: src.utm_source,
			mdm: src.utm_medium,
			cmp: src.utm_campaign,
			trm: src.utm_term,
			cnt: src.utm_content
		}), ttlDays);

		this.setCookie(this.COOKIE_NAME_ADD, JSON.stringify({
			gclid: src.gclid
		}), ttlDays);

		localStorage.setItem(this.LS_KEY, JSON.stringify(src));
	}

	private getStoredSource(): TrafficSource | null {
		const raw = localStorage.getItem(this.LS_KEY);
		return raw ? (JSON.parse(raw) as TrafficSource) : null;
	}

	private getCookie(name: string): string | null {
		const match = this.doc.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
		return match ? decodeURIComponent(match[2]) : null;
	}

	private setCookie(name: string, value: string, days: number): void {
		let encoded = encodeURIComponent(value);
		if (encoded.length > this.MAX_COOKIE_LENGTH) {
			encoded = encoded.slice(0, this.MAX_COOKIE_LENGTH);
		}

		const expires = new Date(Date.now() + days * 864e5).toUTCString();
		const secureFlag = this.doc.location.protocol === 'https:' ? '; Secure' : '';

		this.doc.cookie =
			`${name}=${encoded}; path=/; expires=${expires}; SameSite=Lax${secureFlag}`;
	}
}
